<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import { KANBAN_VISIBLE_LABELS } from '../constants';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['open', 'contextmenu']);

const inboxById = useMapGetter('inboxes/getInbox');
const accountLabels = useMapGetter('labels/getLabels');

const contact = computed(() => props.conversation.meta?.sender || {});
const assignee = computed(() => props.conversation.meta?.assignee);
const inbox = computed(() => inboxById.value(props.conversation.inbox_id));

const preview = computed(() => {
  const lastNonActivity = props.conversation.last_non_activity_message;
  const lastMessage = props.conversation.messages?.[0];
  return lastNonActivity?.content || lastMessage?.content || '';
});

const lastActivity = computed(() => {
  const timestamp = props.conversation.timestamp;
  return timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';
});

const unreadCount = computed(() => props.conversation.unread_count || 0);

const visibleLabels = computed(() => {
  const titles = props.conversation.labels || [];
  return accountLabels.value
    .filter(item => titles.includes(item.title))
    .slice(0, KANBAN_VISIBLE_LABELS);
});

const extraLabelCount = computed(() => {
  if (!visibleLabels.value.length) return 0;
  const total = (props.conversation.labels || []).length;
  return Math.max(0, total - visibleLabels.value.length);
});

const hasInbox = computed(() => Boolean(inbox.value?.id));

const conversationIdLabel = computed(() => `#${props.conversation.id}`);
const extraLabelsLabel = computed(() => `+${extraLabelCount.value}`);
</script>

<template>
  <button
    type="button"
    class="flex w-full flex-col gap-2 rounded-xl border border-n-weak bg-n-solid-1 p-3 text-start shadow-sm transition-colors duration-200 hover:border-n-brand hover:bg-n-solid-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
    @click="emit('open')"
    @contextmenu.prevent="emit('contextmenu', $event)"
  >
    <div class="flex items-start gap-2">
      <Avatar
        :src="contact.thumbnail"
        :name="contact.name || ''"
        :size="28"
        rounded-full
      />
      <div class="min-w-0 flex-1">
        <div class="flex items-center justify-between gap-2">
          <p class="truncate text-sm font-medium text-n-slate-12">
            {{ contact.name }}
          </p>
          <div class="flex shrink-0 items-center gap-1.5">
            <CardPriorityIcon :priority="conversation.priority || null" />
            <span class="text-xs tabular-nums text-n-slate-10">
              {{ lastActivity }}
            </span>
          </div>
        </div>
        <p class="mt-0.5 line-clamp-2 min-h-8 text-xs text-n-slate-11">
          {{ preview }}
        </p>
      </div>
    </div>
    <div
      v-if="conversation.kanban_note"
      class="flex w-full items-start gap-1.5 rounded-md bg-n-amber-3/40 px-2 py-1.5 text-start"
    >
      <span
        class="i-lucide-sticky-note mt-0.5 size-3 shrink-0 text-n-amber-11"
      />
      <p class="mb-0 line-clamp-2 text-xxs text-n-slate-12">
        {{ conversation.kanban_note }}
      </p>
    </div>
    <div v-if="visibleLabels.length" class="flex flex-wrap items-center gap-1">
      <span
        v-for="item in visibleLabels"
        :key="item.id"
        class="inline-flex max-w-28 items-center gap-1 rounded-md bg-n-alpha-2 px-1.5 py-0.5"
      >
        <span
          class="size-1.5 shrink-0 rounded-sm"
          :style="{ backgroundColor: item.color }"
        />
        <span class="truncate text-xxs text-n-slate-12">
          {{ item.title }}
        </span>
      </span>
      <span v-if="extraLabelCount" class="text-xxs text-n-slate-10">
        {{ extraLabelsLabel }}
      </span>
    </div>
    <div class="flex items-center gap-2">
      <span class="text-xxs tabular-nums text-n-slate-10">
        {{ conversationIdLabel }}
      </span>
      <span
        v-if="hasInbox"
        v-tooltip.top="inbox.name"
        class="inline-flex size-5 items-center justify-center rounded-full bg-n-alpha-2"
      >
        <ChannelIcon :inbox="inbox" class="size-3 text-n-slate-11" />
      </span>
      <div class="ms-auto flex min-w-0 items-center gap-1.5">
        <template v-if="assignee">
          <Avatar
            :src="assignee.thumbnail"
            :name="assignee.name || ''"
            :size="16"
            rounded-full
          />
          <span class="max-w-24 truncate text-xs text-n-slate-11">
            {{ assignee.name }}
          </span>
        </template>
        <span v-else class="text-xs text-n-slate-10">
          {{ $t('KANBAN.UNASSIGNED') }}
        </span>
        <span
          v-if="unreadCount"
          class="inline-flex min-w-5 items-center justify-center rounded-full bg-n-brand px-1.5 text-xxs font-medium text-white"
        >
          {{ unreadCount }}
        </span>
      </div>
    </div>
  </button>
</template>
